<script lang="ts">
	import "carbon-components-svelte/css/all.css";
	import {
		Button,
		Column,
		Content,
		Grid,
		Header,
		HeaderUtilities,
		HeaderGlobalAction,
		Link,
		LocalStorage,
		RadioButtonGroup,
		RadioButton,
		Row,
		Select,
		SelectItem,
		SkipToContent,
		TextArea,
	} from "carbon-components-svelte";
	import LogoTwitter24 from "carbon-icons-svelte/lib/LogoTwitter24";
	import CloudDownload16 from "carbon-icons-svelte/lib/CloudDownload16/CloudDownload16.svelte";

	import ipv4Example from "./examples/ipv4.pd";
	import tcpExample from "./examples/tcp.pd";
	import arpExample from "./examples/arp.pd";

	export let renderer;
	console.log(renderer);

	let isSideNavOpen = false;

	let packetDefinition;
	let packetDefinitionError = "";

	let selectedExample = "none";
	$: loadExample(selectedExample);

	let svg;
	let unableToDownload = true;
	$: unableToDownload = !svg;

	let theme = "white"; // "white" | "g10" | "g80" | "g90" | "g100"
	$: document.documentElement.setAttribute("theme", theme);

	$: svg = render_if_valid(packetDefinition);

	function render_if_valid(packetDefinition) {
		if (!packetDefinition) {
			return null;
		}
		try {
			renderer.validate(packetDefinition);
			packetDefinitionError = "";
		} catch (err) {
			packetDefinitionError = err;
			console.error(err);
			return null;
		}
		try {
			return renderer.render(packetDefinition);
		} catch (err) {
			console.error(err);
			return null;
		}
	}

	function loadExample(selected) {
		if (!selected) {
			return;
		}
		if (packetDefinition) {
			if (!confirm("overwrite the current packet definition?")) {
				return;
			}
		}

		switch (selected) {
			case "ipv4":
				packetDefinition = ipv4Example.trim();
				return;
			case "tcp":
				packetDefinition = tcpExample.trim();
				return;
			case "arp":
				packetDefinition = arpExample.trim();
				return;
			default:
				return;
		}
	}

	function downloadSvg() {
		const filename = "packet-diagram.svg";
		const blob = new Blob([svg], { type: "image/svg" });
		if (window.navigator.msSaveOrOpenBlob) {
			window.navigator.msSaveBlob(blob, filename);
		} else {
			const elem = window.document.createElement("a");
			elem.href = window.URL.createObjectURL(blob);
			elem.download = filename;
			document.body.appendChild(elem);
			elem.click();
			document.body.removeChild(elem);
		}
	}

	function shareWithTwitter() {
		window.open(
			"https://twitter.com/intent/tweet?text=Generated%20a%20packet%20diagram%20with%20https://packet-diagram-renderer.bitbears.dev%20created%20by%20@b17bea125",
			"_blank"
		);
	}
</script>

<div class="container">
	<Header
		company="bitbears.dev"
		platformName="Packet Diagram Renderer"
		bind:isSideNavOpen
	>
		<div slot="skip-to-content">
			<SkipToContent />
		</div>
		<HeaderUtilities>
			<HeaderGlobalAction
				aria-label="Share with Twitter"
				icon={LogoTwitter24}
				on:click={shareWithTwitter}
			/>
		</HeaderUtilities>
	</Header>

	<Content>
		<Grid condensed fullWidth>
			<Row>
				<Column md={4}>
					<Grid condensed style="padding-left: 0">
						<Row>
							<Column>
								<div class="textarea-container">
									<TextArea
										invalid={packetDefinitionError != ""}
										invalidText={packetDefinitionError}
										rows={20}
										labelText="Packet definition"
										bind:value={packetDefinition}
									/>
								</div>
								<LocalStorage
									key="packet-definition"
									bind:value={packetDefinition}
								/>
							</Column>
						</Row>
						<Row>
							<Column>
								<Link
									size="sm"
									href="https://bitbears.notion.site/Packet-Diagram-Renderer-Packet-Definition-Reference-cfa84653ad8c4c28b8d1b64e450fa989"
									target="_blank">Reference</Link
								>
							</Column>
						</Row>
						<Row>
							<Column>
								<p>&nbsp;</p>
							</Column>
						</Row>
						<Row>
							<Column>
								<Select labelText="Examples" bind:selected={selectedExample}>
									{#if selectedExample === "none"}
										<SelectItem value="none" text="-" />
									{/if}
									<SelectItem value="ipv4" text="IPv4 header" />
									<SelectItem value="tcp" text="TCP header" />
									<SelectItem value="arp" text="ARP" />
								</Select>
							</Column>
						</Row>
					</Grid>
				</Column>

				<Column md={4}>
					<Grid>
						<Row>
							<Column style="max-width: 100%; overflow: auto">
								<div>
									<label class="bx--label">Preview</label>
								</div>
								{#if svg}
									{@html svg}
								{/if}
							</Column>
						</Row>
						<Row>
							<Column>
								<p>&nbsp;</p>
							</Column>
						</Row>
						<Row>
							<Column>
								<Button
									icon={CloudDownload16}
									bind:disabled={unableToDownload}
									on:click={downloadSvg}
								>
									Download as SVG
								</Button>
							</Column>
						</Row>
						<Row>
							<Column>
								<p>&nbsp;</p>
							</Column>
						</Row>
						<Row>
							<Column>
								<iframe
									src="https://rcm-fe.amazon-adsystem.com/e/cm?o=9&p=13&l=ez&f=ifr&linkID=356a995e255c0176509629f495ccdc39&t=0xb17bea125-22&tracking_id=0xb17bea125-22"
									width="468"
									height="60"
									scrolling="no"
									border="0"
									marginwidth="0"
									style="border:none;"
									frameborder="0"
								/>
							</Column>
						</Row>
					</Grid>
				</Column>
			</Row>
			<Row>
				<Column>
					<p>&nbsp;</p>
				</Column>
			</Row>
			<Row>
				<Column>
					<RadioButtonGroup legendText="Theme" bind:selected={theme}>
						<RadioButton labelText="Light" value="white" />
						<RadioButton labelText="Dark" value="g90" />
					</RadioButtonGroup>
					<LocalStorage key="theme" bind:value={theme} />
				</Column>
			</Row>
		</Grid>
	</Content>
	<footer class="bx--content">
		<Grid>
			<Row>
				<Column>
					<p class="creator">
						Created by: <Link
							href="https://twitter.com/0xb17bea125"
							target="_blank">t@bitbears.dev</Link
						>
					</p>
					<p>&copy; 2021 bitbears.dev</p>
				</Column>
			</Row>
		</Grid>
	</footer>
</div>

<style lang="scss">
	.container {
		display: flex;
		flex-direction: column;
		min-height: 100vh;
	}
	footer {
		margin-top: auto !important;
		background: var(--cds-ui-01);

		p.creator {
			font-weight: bolder !important;
		}
	}
	.textarea-container {
		min-width: 50%;
		font-family: "Courier New", Courier, monospace;
		font-size: 9pt;
	}
</style>
